import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type { CourtesyCar, ContractType, CourtesyCarStatus, CarAssignmentFull, AssignmentStatus } from '@/types'
import { usePermission } from '@/hooks/usePermission'

// ALLINEATO AL BACKEND MODEL
interface CourtesyCarCreate {
  vehicle_id: number
  contratto_tipo: ContractType
  fornitore_contratto?: string
  data_inizio_contratto?: string
  data_scadenza_contratto?: string
  canone_mensile?: number
  km_inclusi_anno?: number
  stato: CourtesyCarStatus
  note?: string
}

export const useCourtesyCars = (page: number = 1, size: number = 10, search?: string, stato?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['courtesy-cars', page, size, search, stato],
    queryFn: async () => {
      const params = new URLSearchParams({
        skip: String((page - 1) * size),
        limit: String(size),
      })
      if (search) {
        params.append('search', search)
      }
      if (stato) {
        params.append('stato_filter', stato)
      }
      const response = await axiosInstance.get<CourtesyCar[]>(
        `${API_ENDPOINTS.COURTESY_CARS}/?${params.toString()}`
      )
      // L'API ritorna un array direttamente, non un oggetto paginato
      // Ritorniamo nel formato che il componente aspetta
      return {
        items: Array.isArray(response.data) ? response.data : [],
        total: Array.isArray(response.data) ? response.data.length : 0,
        page: page,
        pages: 1,
      }
    },
    refetchInterval: 60_000,
    enabled: options?.enabled ?? true,
  })
}

export const useCourtesyCar = (id: number) => {
  return useQuery({
    queryKey: ['courtesy-car', id],
    queryFn: async () => {
      const response = await axiosInstance.get<CourtesyCar>(
        `${API_ENDPOINTS.COURTESY_CARS}/${id}`
      )
      return response.data
    },
    enabled: !!id,
  })
}

export const useCreateCourtesyCar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CourtesyCarCreate) => {
      const response = await axiosInstance.post<CourtesyCar>(
        `${API_ENDPOINTS.COURTESY_CARS}/`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars'] })
      message.success('Auto cortesia creata con successo')
    },
    onError: () => {
      message.error('Errore durante la creazione dell\'auto cortesia')
    },
  })
}

export const useUpdateCourtesyCar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CourtesyCarCreate> }) => {
      const response = await axiosInstance.put<CourtesyCar>(
        `${API_ENDPOINTS.COURTESY_CARS}/${id}`,
        data
      )
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars'] })
      queryClient.invalidateQueries({ queryKey: ['courtesy-car', variables.id] })
      message.success('Auto cortesia aggiornata con successo')
    },
    onError: () => {
      message.error('Errore durante l\'aggiornamento dell\'auto cortesia')
    },
  })
}

export const useDeleteCourtesyCar = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${API_ENDPOINTS.COURTESY_CARS}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars'] })
      message.success('Auto cortesia eliminata con successo')
    },
    onError: () => {
      message.error('Errore durante l\'eliminazione dell\'auto cortesia')
    },
  })
}

export const useAvailableCourtesyCars = (dateFrom?: string, dateTo?: string, excludeAssignmentId?: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['courtesy-cars', 'available', dateFrom, dateTo, excludeAssignmentId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      if (excludeAssignmentId) params.append('exclude_assignment_id', String(excludeAssignmentId))
      const response = await axiosInstance.get<CourtesyCar[]>(
        `${API_ENDPOINTS.COURTESY_CARS}/available?${params.toString()}`
      )
      return response.data
    },
    enabled: options?.enabled ?? true,
  })
}

export const useWorkOrderCarAssignment = (workOrderId?: number) => {
  const canView = usePermission('courtesy_cars.view')
  return useQuery({
    queryKey: ['car-assignments', 'by-wo', workOrderId],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('work_order_id', String(workOrderId))
      const response = await axiosInstance.get<CarAssignmentFull[]>(
        `${API_ENDPOINTS.COURTESY_CARS}/assignments?${params.toString()}`
      )
      // Ritorna la più recente assegnazione attiva (PRENOTATA o IN_CORSO), poi le altre
      const sorted = response.data.sort((a, b) => {
        const priority = (s: string) => s === 'IN_CORSO' ? 0 : s === 'PRENOTATA' ? 1 : 2
        return priority(a.stato) - priority(b.stato)
      })
      return sorted[0] ?? null
    },
    enabled: !!workOrderId && canView,
    staleTime: 30000,
  })
}

export const useCarAssignments = (
  dateFrom?: string,
  dateTo?: string,
  filters?: { courtesy_car_id?: number; stato?: AssignmentStatus },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['car-assignments', dateFrom, dateTo, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      if (filters?.courtesy_car_id) params.append('courtesy_car_id', String(filters.courtesy_car_id))
      if (filters?.stato) params.append('stato', filters.stato)
      const response = await axiosInstance.get<CarAssignmentFull[]>(
        `${API_ENDPOINTS.COURTESY_CARS}/assignments?${params.toString()}`
      )
      return response.data
    },
    enabled: options?.enabled ?? true,
  })
}

interface CarAssignmentCreatePayload {
  courtesy_car_id: number
  customer_id: number
  work_order_id?: number
  data_inizio: string
  data_fine_prevista: string
  km_inizio?: number
  note?: string
}

interface CarAssignmentUpdatePayload {
  courtesy_car_id?: number
  customer_id?: number
  data_inizio?: string
  data_fine_prevista?: string
  data_fine_effettiva?: string
  km_inizio?: number
  km_fine?: number
  stato?: AssignmentStatus
  note?: string
}

export const useCreateCarAssignment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CarAssignmentCreatePayload) => {
      const response = await axiosInstance.post<CarAssignmentFull>(
        `${API_ENDPOINTS.COURTESY_CARS}/assignments`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['car-assignments'] })
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars', 'available'] })
      queryClient.invalidateQueries({ queryKey: ['adc-da-consegnare'] })
      queryClient.invalidateQueries({ queryKey: ['adc-da-riconsegnare'] })
      message.success('Assegnazione creata')
    },
    onError: (err: any) => {
      message.error(err.response?.data?.detail || 'Errore creazione assegnazione')
    },
  })
}

export const useUpdateCarAssignment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CarAssignmentUpdatePayload }) => {
      const response = await axiosInstance.put<CarAssignmentFull>(
        `${API_ENDPOINTS.COURTESY_CARS}/assignments/${id}`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['car-assignments'] })
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars', 'available'] })
      queryClient.invalidateQueries({ queryKey: ['adc-da-consegnare'] })
      queryClient.invalidateQueries({ queryKey: ['adc-da-riconsegnare'] })
      message.success('Assegnazione aggiornata')
    },
    onError: (err: any) => {
      message.error(err.response?.data?.detail || 'Errore aggiornamento assegnazione')
    },
  })
}

export const useDeleteCarAssignment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${API_ENDPOINTS.COURTESY_CARS}/assignments/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['car-assignments'] })
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars', 'available'] })
      queryClient.invalidateQueries({ queryKey: ['adc-da-consegnare'] })
      queryClient.invalidateQueries({ queryKey: ['adc-da-riconsegnare'] })
      message.success('Assegnazione eliminata')
    },
    onError: (err: any) => {
      message.error(err.response?.data?.detail || 'Errore eliminazione assegnazione')
    },
  })
}

// UPLOAD contract PDF
export const useUploadContratto = (carId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await axiosInstance.post(
        `${API_ENDPOINTS.COURTESY_CARS}/${carId}/contratto`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars', carId] })
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars'] })
      message.success('Contratto caricato con successo')
    },
    onError: () => {
      message.error('Errore durante il caricamento del contratto')
    },
  })
}

// DOWNLOAD contract PDF
export const useDownloadContratto = (carId: number) => {
  return useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.COURTESY_CARS}/${carId}/contratto/download`,
        {
          responseType: 'blob',
        }
      )
      
      // Crea un URL temporaneo e scarica il file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `contratto_${carId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    onSuccess: () => {
      message.success('Contratto scaricato con successo')
    },
    onError: () => {
      message.error('Errore durante lo scaricamento del contratto')
    },
  })
}

// DELETE contract PDF
export const useDeleteContratto = (carId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(
        `${API_ENDPOINTS.COURTESY_CARS}/${carId}/contratto`
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars', carId] })
      queryClient.invalidateQueries({ queryKey: ['courtesy-cars'] })
      message.success('Contratto eliminato con successo')
    },
    onError: () => {
      message.error('Errore durante l\'eliminazione del contratto')
    },
  })
}
